/**
 * VGrove UI Client - 新一代标准化 API
 * 基于 React Router v6+ loader/action 模式的现代化路由认证和权限管理
 */

// ============= 新一代核心 API =============

// === 核心 Loader API ===
export {
  createAuthLoader,
  createRoleLoader,
  createPermissionLoader,
  createCombinedLoader,
  createCustomLoader,
  combineLoaders,
  configureLoaders,
  type AuthCheck,
  type RoleCheck,
  type PermissionCheck,
  type LoaderOptions
} from './loaders';

// === 核心 Action API ===
export {
  createAction,
  createLoginAction,
  createRegisterAction,
  createLogoutAction,
  createCrudAction,
  createFileUploadAction,
  combineActions,
  configureActions,
  type ValidationError,
  type ActionResponse,
  type FormValidator,
  type DataProcessor,
  type ActionOptions
} from './actions';

// === 内部导入用于 quickSetup ===
import { createAuthLoader, configureLoaders } from './loaders';
import { createLoginAction, createLogoutAction, configureActions } from './actions';
import { updateDebugConfig } from './debug';
import type { BaseUser } from './types';

// ============= 现有 API =============

// ============= 导出所有类型 =============
export * from './types';

// ============= 导出React Hooks（排除冲突的类型）=============
export * from './hooks';

// ============= 导出工具函数（排除throttle冲突）=============
export * from './utils';

// ============= 导出性能优化工具 =============
export * from './performance';

// ============= 导出配置管理 =============
export * from './config';

// ============= 导出调试工具 =============
export * from './debug';

// ============= 导出国际化支持 =============
export * from './i18n';
export * from './i18n-loader';

// ============= 导出国际化路由集成 =============
export * from './i18n-route-loader';

// ============= 导出中间件 =============
export * from './middleware';

// ============= 导出增强 Loaders =============
export * from './enhanced-loaders';

// ============= 导出 VGrove Autoroutes 集成 =============
export * from './autoroutes-integration';
export { default as AutoroutesIntegration } from './autoroutes-integration';

// ============= 旧 API 已移除 =============
// 使用新的 loader/action 系统替代

// ============= 版本信息 =============
export const VERSION = '2.0.0';
export const API_VERSION = 'v2';

// ============= 快速开始配置 =============
/**
 * 快速配置 VGrove 认证系统
 * 
 * @example
 * ```typescript
 * import { quickSetup } from '@gulibs/vgrove-ui';
 * 
 * const { authLoader, loginAction, logoutAction } = quickSetup({
 *   authCheck: () => getCurrentUser(),
 *   loginApi: async (credentials) => await login(credentials),
 *   loginPath: '/login',
 *   publicPaths: ['/login', '/register']
 * });
 * 
 * // 在路由中使用
 * const router = createBrowserRouter([
 *   {
 *     path: '/dashboard',
 *     element: <Dashboard />,
 *     loader: authLoader
 *   },
 *   {
 *     path: '/login',
 *     element: <Login />,
 *     action: loginAction
 *   }
 * ]);
 * ```
 */
export function quickSetup<TUser extends BaseUser = BaseUser, TCredentials = any>(config: {
  /** 认证检查函数 */
  authCheck: () => Promise<TUser | null> | TUser | null;
  /** 登录 API */
  loginApi?: (credentials: TCredentials) => Promise<{ user: TUser; token: string }>;
  /** 登出处理函数 */
  logoutHandler?: () => Promise<void>;
  /** 登录页面路径 */
  loginPath?: string;
  /** 禁止访问页面路径 */
  forbiddenPath?: string;
  /** 公共路径列表 */
  publicPaths?: string[];
  /** 是否启用调试 */
  debug?: boolean;
}) {

  // 配置调试
  if (config.debug) {
    updateDebugConfig({
      enabled: true,
      auth: true,
      loader: true,
      action: true
    });
  }

  // 配置全局选项
  configureLoaders<TUser>({
    authCheck: config.authCheck,
    loginPath: config.loginPath || '/login',
    forbiddenPath: config.forbiddenPath || '/403',
    publicPaths: config.publicPaths || ['/login', '/register', '/404', '/403']
  });

  configureActions<TUser>({
    authCheck: config.authCheck,
    requireAuth: true
  });

  // 创建基础 loader 和 action
  const authLoader = createAuthLoader<TUser>();

  const loginAction = config.loginApi
    ? createLoginAction<TUser, TCredentials>(config.loginApi)
    : undefined;

  const logoutAction = createLogoutAction(config.logoutHandler);

  return {
    authLoader,
    loginAction,
    logoutAction
  };
}
